import { getRoot, types } from 'mobx-state-tree';
import ChatSession from './models/HccAIChat';
import { processChatRequest } from 'api/hccAi';
import { addChatRecord } from 'api/uploadToZapier';
import {
  convertTranscriptToMarkdown,
  convertTranscriptToPlainText,
} from 'utils/convertTranscriptToMarkdown';

const defaultChatSession = {
  sessionId: '',
  transcript: [],
  responses: 0,
  likes: 0,
  dislikes: 0,
  botName: 'HCC Help Bot',
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
};

const getInitialPrompt = ({ term }) => {
  const formatDate = (date) => date.toDateString();
  const currentDate = new Date();
  const TOTAL_WEEKS = 20;

  const termStartDate = new Date(term.startDate);

  // Calculate end date (20 weeks from start date)
  const termEndDate = new Date(termStartDate);
  termEndDate.setDate(termStartDate.getDate() + TOTAL_WEEKS * 7);

  const isCohortOngoing = currentDate <= termEndDate;
  const cohortStatus = isCohortOngoing ? 'in progress' : 'concluded';

  return `
  ## User Details
  This user is in Cohort ${term.id}

  ## Cohort Information:
  Start Date: ${formatDate(termStartDate)}
  End Date: ${formatDate(termEndDate)}
  Status: ${cohortStatus}
  Today's Date: ${formatDate(currentDate)}
  `;
};

export const HccAiStore = types
  .model('HccAiStore', {
    chatSession: types.optional(ChatSession, defaultChatSession),
    loading: false,
    submitting: false,
    newConversationAdded: false,
    inputHasError: '',
    currentSessionId: types.maybeNull(types.string),
  })
  .views((self) => ({
    get currentTranscript() {
      return self.chatSession.transcript;
    },
    getTranscriptEntry(id) {
      return self.currentTranscript.find((entry) => entry.id === id);
    },
  }))
  .actions((self) => ({
    async provideChatGeneralInformation(userId) {
      self.setLoading(true);
      try {
        const prompt = getInitialPrompt({
          term: getRoot(self).classDetailsStore.activeClass.term,
        });

        const response = await processChatRequest({
          input: prompt,
          sessionId: self.currentSessionId || '',
          isInitialPrompt: true,
          userId,
        });

        self.setCurrentSessionId(response.processChatRequest.sessionState.sessionId);
      } catch (err) {
        console.error('Error giving chat general information:', err);
      } finally {
        self.setLoading(false);
      }
    },
    startConversation(conversation, currentUser) {
      self.loading = true;
      self.submitting = true;
      self.newConversationAdded = true;

      if (self.chatSession.responses === 0) {
        self.chatSession = {
          ...self.chatSession,
          userId: currentUser.userId,
          email: currentUser.emailAddress,
        };
      }

      self.chatSession.sessionId = self.currentSessionId || '';
      self.chatSession.transcript.push({
        id: conversation.id,
        question: conversation.question,
      });

      self.submitting = true;
      self.getResponse(conversation, currentUser.userId);
    },

    async getResponse(conversation, userId) {
      try {
        const response = await processChatRequest({
          input: conversation.question,
          sessionId: self.currentSessionId || '',
          userId,
        });
        self.applyChatResponse(conversation.id, response);
      } catch (error) {
        console.error('Error processing chat request:', error.message);
        self.responseHasError();
      }
    },

    applyChatResponse(id, response) {
      const chatResult = response?.processChatRequest;
      if (!chatResult?.messages?.length) {
        console.error('applyChatResponse called with no processChatRequest payload', response);
        self.responseHasError();
        return;
      }

      const responseContent = chatResult.messages[0].content;
      self.currentSessionId = chatResult.sessionState.sessionId;

      const transcriptEntry = self.getTranscriptEntry(id);
      if (transcriptEntry) {
        transcriptEntry.response = responseContent;

        self.chatSession.responses += 1;

        if (self.chatSession.sessionId === '') {
          self.chatSession.sessionId = self.currentSessionId;
        }
      }
      self.stopLoading();
      self.syncWithZapier();
    },

    async syncWithZapier() {
      try {
        const transcriptAsMarkdown = convertTranscriptToMarkdown(self.chatSession.transcript);
        const transcriptAsPlainText = convertTranscriptToPlainText(self.chatSession.transcript);

        await addChatRecord({
          input: {
            ...self.chatSession,
            transcript: transcriptAsMarkdown,
            plainTextTranscript: transcriptAsPlainText,
          },
        });
      } catch (error) {
        console.error('Error adding chat record to Zapier:', error);
      }
    },

    setCurrentSessionId(sessionId) {
      self.currentSessionId = sessionId;
    },

    resetNewConversationAdded() {
      self.newConversationAdded = false;
    },

    stopLoading() {
      self.loading = false;
      self.submitting = false;
    },

    setInputError(value) {
      self.inputHasError = value;
    },

    responseHasError() {
      self.stopLoading();
    },

    setResponseFeedback(transcriptId, feedback) {
      const transcriptEntry = self.getTranscriptEntry(transcriptId);
      if (transcriptEntry) {
        const currentFeedback = transcriptEntry.userFeedback;
        transcriptEntry.userFeedback = feedback;

        if (currentFeedback !== feedback) {
          self.updateFeedbackCounts(currentFeedback, feedback);
        }

        self.syncWithZapier();
      }
    },

    updateFeedbackCounts(oldFeedback, newFeedback) {
      if (newFeedback === 'positive') {
        self.chatSession.likes += 1;
        if (oldFeedback === 'negative') {
          self.chatSession.dislikes -= 1;
        }
      } else {
        self.chatSession.dislikes += 1;
        if (oldFeedback === 'positive') {
          self.chatSession.likes -= 1;
        }
      }
    },

    setLoading(loading) {
      self.loading = loading;
    },
  }));
